import { chain, uniq } from "lodash";
import { isDefined } from "utils/isDefined";
import { EuiBadgeGroup, EuiFlexGroup, EuiFlexItem } from "@inscopix/ideas-eui";
import { memo, useEffect, useState } from "react";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { IdeasFileNotFound } from "@inscopix/ideas-hyperformula";
import { CellValueRendererFile } from "pages/gdt/cell-value-renderers/CellValueRendererFile";
import { CellValueRendererRowIdentifier } from "pages/gdt/cell-value-renderers/CellValueRendererRowIdentifier";
import { MetadatumReferenceSelectorGdtProps } from "./MetadatumReferenceSelectorGdt";
import { MetadataTableGdt } from "components/ToolParamsGrid/GridRendererToolParam/MetadataTableGdt";
import { getFileMetadata } from "components/ToolParamsGrid/GridRendererToolParam/MetadataTableFile.helpers";

type Metadatum = Awaited<ReturnType<typeof getFileMetadata>>[number];

interface MetadataTableFileGdtProps {
  fileIds: string[];
  onSelect: (
    metadatumKey: string,
    value: string | number | boolean | null,
  ) => void;
  currentReference: MetadatumReferenceSelectorGdtProps["currentReference"];
}

/** Component that renders a table of file metadata */
export const MetadataTableFileGdt = memo(function MetadataTableFileGdt({
  fileIds: sourceFileIds,
  onSelect,
  currentReference,
}: MetadataTableFileGdtProps) {
  const fileMap = useDataTableContext((s) => s.fileMap);
  const [metadata, setMetadata] = useState<Metadatum[]>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error>();

  const getFileAssignments = useDataTableContext((s) => s.getFileReferences);

  // Fetch data any time file IDs change
  useEffect(() => {
    setIsLoading(true);
    getFileMetadata(sourceFileIds)
      .then(setMetadata)
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, [sourceFileIds]);

  // Create a map of metadatum values by key
  const metadataByKey = chain(metadata)
    .groupBy(({ key }) => key)
    .value();

  // Create an array of metadatum objects from the response data. If multiple
  // files have values for the same metadatum, their values are stored
  // together. We remove any metadata that does not have a name. This occurs
  // when a metadatum is not part of the `metadataMap`.
  const items = chain(metadataByKey)
    .values()
    .map((metadata) => {
      const key = metadata[0].key;
      const name = metadata[0].name;
      const values = metadata
        .map(({ value }) => value)
        .filter(
          (value): value is string | number | boolean | null =>
            typeof value === "string" ||
            typeof value === "number" ||
            typeof value === "boolean" ||
            value === null,
        );
      return {
        key,
        name,
        values: uniq(values),
      };
    })
    .value();

  return (
    <MetadataTableGdt
      isLoading={isLoading}
      isError={error !== undefined}
      items={items}
      onSelect={onSelect}
      currentReference={currentReference}
      renderValueTooltip={(metadatumKey, value) => {
        const files = metadataByKey[metadatumKey]
          .filter((metadatum) => metadatum.value === value)
          .map(({ fileId }) => fileMap.filesById[fileId])
          .filter(
            (file): file is Exclude<typeof file, typeof IdeasFileNotFound> =>
              file !== IdeasFileNotFound,
          )
          .filter(isDefined);

        // Do not show a tooltip unless there are multiple files selected
        // in the source column
        if (sourceFileIds.length < 2 || files.length === 0) {
          return null;
        }

        return (
          <EuiFlexGroup direction="column" gutterSize="xs">
            <EuiFlexItem>
              <strong>File(s)</strong>
            </EuiFlexItem>
            {files.map((file) => {
              const identifiers = getFileAssignments(file.id);

              return (
                <EuiFlexGroup key={file.id} alignItems="center" gutterSize="xs">
                  {identifiers.length > 0 && (
                    <EuiBadgeGroup>
                      {identifiers.map((identifier) => (
                        <CellValueRendererRowIdentifier
                          key={`${identifier.tableKey}-${identifier.rowIndex}`}
                          rowIdentifier={identifier}
                        />
                      ))}
                    </EuiBadgeGroup>
                  )}

                  <EuiFlexItem>
                    <CellValueRendererFile fileId={file.id} />
                  </EuiFlexItem>
                </EuiFlexGroup>
              );
            })}
          </EuiFlexGroup>
        );
      }}
    />
  );
});
